import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  List,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconCheck, IconShieldCheck } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { FinalVerdict } from "@/lib/api/examinations";
import { getPreflight, issueVerdict, type VerdictIssuePayload } from "@/lib/api/verdict";

export function VerdictForm({ examId }: { examId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: preflight, isLoading } = useQuery({
    queryKey: ["verdict-preflight", examId],
    queryFn: () => getPreflight(examId),
  });

  const form = useForm({
    initialValues: {
      diagnosis_summary: "",
      profession_fitness: "fit" as "fit" | "unfit",
      pregnancy_relevant: "no" as "yes" | "no",
      referral_kind: "none" as "none" | "stationary" | "outpatient" | "sanatorium" | "sankurort",
      referral_notes: "",
      final_decision: "fit" as "fit" | "unfit",
      notes: "",
      valid_until: null as Date | null,
    },
    validate: {
      diagnosis_summary: (v) =>
        v.trim().length > 0 ? null : t("validation.required"),
      notes: (v) => (v.trim().length > 0 ? null : t("validation.required")),
    },
  });

  const mutation = useMutation({
    mutationFn: (payload: VerdictIssuePayload) => issueVerdict(examId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["examination", examId] });
      queryClient.invalidateQueries({ queryKey: ["verdict-preflight", examId] });
      queryClient.invalidateQueries({ queryKey: ["examinations"] });
      notifications.show({ color: "green", message: t("verdict.issued") });
      form.reset();
    },
  });

  const onSubmit = form.onSubmit((vals) => {
    const verdict: FinalVerdict =
      vals.final_decision === "unfit" ? "unfit_for_work" : "healthy";
    mutation.mutate({
      verdict,
      notes: vals.notes,
      valid_until: vals.valid_until ? vals.valid_until.toISOString().slice(0, 10) : null,
      diagnosis_summary: vals.diagnosis_summary,
      profession_fitness: vals.profession_fitness,
      pregnancy_relevant: vals.pregnancy_relevant === "yes",
      referral_kind: vals.referral_kind,
      referral_notes: vals.referral_notes || null,
      final_decision: vals.final_decision,
    });
  });

  return (
    <Card padding="xl" radius="xl">
      <Group gap="sm" mb="md">
        <ThemeIcon size={36} radius="xl" variant="light" color="brand">
          <IconShieldCheck size={18} strokeWidth={2.4} />
        </ThemeIcon>
        <Title order={5} fw={700}>
          {t("verdict.profpat_title")}
        </Title>
      </Group>

      <Stack>
        {isLoading && <Text size="sm">{t("common.loading")}</Text>}

        {preflight && !preflight.can_issue && (
          <Alert color="orange" icon={<IconAlertCircle size={16} />} radius="lg">
            <Text fw={600}>{t("verdict.cannot_issue")}</Text>
            {preflight.missing_sections.length > 0 && (
              <List size="sm" mt={4}>
                {preflight.missing_sections.map((s) => (
                  <List.Item key={s}>{t(`users.specialties.${s}`)}</List.Item>
                ))}
              </List>
            )}
            {preflight.pending_lab_orders > 0 && (
              <Text size="sm">
                {t("verdict.pending_lab")}: {preflight.pending_lab_orders}
              </Text>
            )}
          </Alert>
        )}

        {preflight?.suggested_contraindications.length ? (
          <Alert color="yellow" radius="lg">
            <Text fw={600} size="sm">
              {t("verdict.suggested_contraindications")}:
            </Text>
            <List size="sm" mt={4}>
              {preflight.suggested_contraindications.map((c) => (
                <List.Item key={c.rule_id}>
                  <Badge size="xs" color="orange" variant="light" mr="xs">
                    {c.severity}
                  </Badge>
                  {c.label}
                </List.Item>
              ))}
            </List>
          </Alert>
        ) : null}

        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Card padding="md" radius="lg" withBorder>
              <Text size="xs" fw={800} tt="uppercase" c="brand.7" mb="sm">
                1. {t("verdict.profpat.diagnosis")}
              </Text>
              <TextInput
                placeholder={t("verdict.profpat.diagnosis_placeholder")}
                {...form.getInputProps("diagnosis_summary")}
              />
            </Card>

            <Card padding="md" radius="lg" withBorder>
              <Text size="xs" fw={800} tt="uppercase" c="brand.7" mb="sm">
                2-3. {t("verdict.profpat.profession_fitness")}
              </Text>
              <Radio.Group {...form.getInputProps("profession_fitness")}>
                <Stack gap="xs">
                  <Radio value="fit" label={t("verdict.profpat.fit_in_profession")} />
                  <Radio value="unfit" label={t("verdict.profpat.unfit_in_profession")} />
                </Stack>
              </Radio.Group>
            </Card>

            <Card padding="md" radius="lg" withBorder>
              <Text size="xs" fw={800} tt="uppercase" c="brand.7" mb="sm">
                4. {t("verdict.profpat.pregnancy")}
              </Text>
              <Radio.Group {...form.getInputProps("pregnancy_relevant")}>
                <Group>
                  <Radio value="yes" label={t("common.yes")} />
                  <Radio value="no" label={t("common.no")} />
                </Group>
              </Radio.Group>
            </Card>

            <Card padding="md" radius="lg" withBorder>
              <Text size="xs" fw={800} tt="uppercase" c="brand.7" mb="sm">
                5. {t("verdict.profpat.referral")}
              </Text>
              <Stack gap="sm">
                <Select
                  data={[
                    { value: "none", label: t("verdict.profpat.referral_none") },
                    { value: "stationary", label: t("verdict.profpat.referral_stationary") },
                    { value: "outpatient", label: t("verdict.profpat.referral_outpatient") },
                    { value: "sanatorium", label: t("verdict.profpat.referral_sanatorium") },
                    { value: "sankurort", label: t("verdict.profpat.referral_sankurort") },
                  ]}
                  {...form.getInputProps("referral_kind")}
                />
                <Textarea
                  placeholder={t("verdict.profpat.referral_notes_placeholder")}
                  minRows={2}
                  autosize
                  {...form.getInputProps("referral_notes")}
                />
              </Stack>
            </Card>

            <Card
              padding="md"
              radius="lg"
              withBorder
              style={{
                background: "var(--mantine-color-brand-0)",
                borderColor: "var(--mantine-color-brand-3)",
              }}
            >
              <Text size="xs" fw={800} tt="uppercase" c="brand.9" mb="sm">
                {t("verdict.profpat.final_decision")}
              </Text>
              <Radio.Group {...form.getInputProps("final_decision")}>
                <Stack gap="xs">
                  <Radio value="fit" label={t("verdict.profpat.final_fit")} />
                  <Radio value="unfit" label={t("verdict.profpat.final_unfit")} />
                </Stack>
              </Radio.Group>
            </Card>

            <Textarea
              label={t("verdict.fields.notes")}
              placeholder={t("placeholder.notes")}
              required
              minRows={2}
              autosize
              {...form.getInputProps("notes")}
            />
            <DateInput
              label={t("verdict.fields.valid_until")}
              placeholder="DD.MM.YYYY"
              valueFormat="DD.MM.YYYY"
              minDate={new Date()}
              clearable
              {...form.getInputProps("valid_until")}
            />

            <Group justify="flex-end" mt="sm">
              <Button
                type="submit"
                leftSection={<IconCheck size={16} strokeWidth={2.4} />}
                loading={mutation.isPending}
                disabled={!preflight?.can_issue}
                radius="xl"
                size="md"
                color="brand.7"
                px={32}
              >
                {t("verdict.profpat.confirm_close")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Card>
  );
}
